import { Button } from '@studio/design-system/button';
import { Logo } from '@studio/design-system/logo';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { Sel, STagNew } from '#~/stag/tag.js';
import { FunnelShell } from './funnel-shell.js';
import { useStepView } from '../hooks/use-step-view.js';

interface FunnelWelcomeProps {
  readonly onNext: () => void;
  readonly onSkip: () => void;
  /** Escape / the close affordance: dismiss WITHOUT recording the permanent
   *  skip. Distinct from onSkip, which is an explicit user decision to defer —
   *  see gate.tsx handleDismiss (ENG-3796 live-run defect 1). */
  readonly onDismiss: () => void;
}

// Screen 1 of the bridge-connection funnel (RFD 0006). Frames the
// cold-launch moment before the user has any context about Bridges.
// Logo + wordmark above the heading matches the PM prototype.
export const FunnelWelcome = ({ onNext, onSkip, onDismiss }: FunnelWelcomeProps): ReactElement => {
  useStepView('welcome');

  return (
    <FunnelShell
      announceTitle={`Welcome to ${vendorName()}`}
      onEscape={onDismiss}
      glow
      progressStep={1}
    >
      <Logo className='mb-8' size='2x' />

      <h1
        className='mt-24 text-3xl font-demibold text-white'
        {...STagNew(Sel.OnboardingWelcome, 'heading')}
      >
        Welcome to {vendorName()}.
      </h1>
      <p className='mt-12 max-w-[28rem] text-md text-foreground/75'>
        Let's get your first camera connected. It takes less than a minute!
      </p>

      <Button
        autoFocus
        className='mt-32'
        {...STagNew(Sel.OnboardingWelcome, 'get_started')}
        onClick={onNext}
        size='lg'
        variant='primary'
      >
        Add your first camera
      </Button>

      <div className='mt-32 w-full max-w-[28rem] border-t border-base-100 pt-20'>
        <button
          // text-foreground/75 (not text-white/70): Tailwind JIT only emits the
          // opacity variants it sees referenced, and text-white/70 is used
          // nowhere else, so it is NOT generated in this build and falls back to
          // black — invisible on the dark panel (ENG-3796). text-foreground/75 is
          // the same token the body copy uses (generated, renders light); the
          // hover:text-foreground variant is also generated (used by the form's
          // Back link), so hover stays legible. Focus-visible ring preserved.
          className='focus-visible:ring-primary/50 text-xs text-foreground/75 transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2'
          {...STagNew(Sel.OnboardingWelcome, 'skip')}
          onClick={onSkip}
          type='button'
        >
          Just let me explore the app &rarr;
        </button>
      </div>
    </FunnelShell>
  );
};
